import { Dispatch, useReducer, useState } from "react";
import { Author } from "../../../../../../types/Author";
import {
  AdPlatform,
  CommunityAdFormat,
  CommunityAdFormatProfile,
  CompetitorReferenceAd,
} from "../../../../../../types/CommunityMediaConfig";
import { DiscoveredAdFormat } from "../../../../../../database/community/mediaConfig";
import {
  AdScript,
  AdCopyVariant,
  AdAngleCategory,
  AdPlacement,
} from "../../../../../../database/community/adsGenerator";
import { SavedAdVariant } from "../../../../../../database/community/adVariants";

const DEFAULT_SELECTED_ANGLES: AdAngleCategory[] = [
  "pain-point",
  "outcome",
  "social-proof",
  "curiosity",
];

export type VariantSelection = {
  formatKey: string;
  placement: AdPlacement;
};

export type VariantImage = {
  dataUrl: string;
  formatKey: string;
  placement: AdPlacement;
};

export type LandingSummary = {
  title: string;
  subtitle: string;
  buttonText: string;
};

export type RefStats = {
  attempted: number;
  succeeded: number;
};

export type ImageProvider = "gpt" | "gemini";

export type AdsGeneratorState = {
  /** Campaign brief, platform/angle selections, and export naming inputs. */
  inputs: {
    campaign: string;
    selectedPlatforms: AdPlatform[];
    selectedAngles: AdAngleCategory[];
    variationsPerAngle: number;
    imageProvider: ImageProvider;
    // Campaign-naming opts (paid-ads naming convention for CSV export).
    namingObjective: string;
    namingAudience: string;
    namingOffer: string;
    // Landing-page summary. When present, the user doesn't need to type a
    // campaign description (we fall back to title + subtitle).
    landingSummary: LandingSummary | null;
  };
  /** Saved ad formats plus the latest research/discovery results. */
  formats: {
    adFormats: CommunityAdFormat[];
    adFormatProfile: CommunityAdFormatProfile | undefined;
    discoveries: DiscoveredAdFormat[];
    researchRationale: string;
    researchSources: string[];
  };
  /** Generated scripts, copy variants, and per-variant records. */
  generation: {
    activeScripts: Record<string, AdScript>;
    variants: AdCopyVariant[];
    /** Keyed by variant index in the `variants` array. */
    variantSelections: Record<number, VariantSelection>;
    variantImages: Record<number, VariantImage>;
    variantSaving: Record<number, boolean>;
    savedVariantIds: Record<number, string>;
    /** Per-variant toggle: bake the hook into the image as a text overlay. */
    variantTextOnImage: Record<number, boolean>;
    /** Per-variant custom overlay text (overrides the auto-clip). */
    variantOverlayText: Record<number, string>;
  };
  /** Library of saved ad variants for this community. */
  library: {
    savedVariants: SavedAdVariant[];
    isLoadingSaved: boolean;
  };
  /** Competitor reference ads (style anchors for image gen, fight AI slop). */
  refs: {
    competitorRefs: CompetitorReferenceAd[];
    isUploadingRef: boolean;
    newRefLabel: string;
    newRefSource: string;
    /** Cache of imageURL -> data URL so we don't refetch on every image gen. */
    refDataUrlCache: Record<string, string>;
    /** URLs we KNOW failed to fetch (CORS, 403, deleted). Renders red on the thumb. */
    refFetchFailures: Record<string, string>;
    /** Last-gen stats so the variant card can show "X of N refs used". */
    lastRefStats: RefStats | null;
  };
  // Author picker. Selected author's face is passed as personReferenceImage
  // to generateAdImage so the ad image renders that exact face (image skill).
  authors: {
    authors: Author[];
    selectedAuthorId: string;
    authorRefDataUrl: string;
    isLoadingAuthorRef: boolean;
  };
  /** Async flags + the shared error banner. */
  status: {
    isResearching: boolean;
    scriptLoadingKey: string | null;
    imageLoadingVariantIdx: number | null;
    isGeneratingCopy: boolean;
    errorMsg: string;
  };
};

const initialState: AdsGeneratorState = {
  inputs: {
    campaign: "",
    selectedPlatforms: ["meta", "tiktok"],
    selectedAngles: DEFAULT_SELECTED_ANGLES,
    variationsPerAngle: 3,
    imageProvider: "gpt",
    namingObjective: "Conv",
    namingAudience: "BroadAudience",
    namingOffer: "Default",
    landingSummary: null,
  },
  formats: {
    adFormats: [],
    adFormatProfile: undefined,
    discoveries: [],
    researchRationale: "",
    researchSources: [],
  },
  generation: {
    activeScripts: {},
    variants: [],
    variantSelections: {},
    variantImages: {},
    variantSaving: {},
    savedVariantIds: {},
    variantTextOnImage: {},
    variantOverlayText: {},
  },
  library: {
    savedVariants: [],
    isLoadingSaved: false,
  },
  refs: {
    competitorRefs: [],
    isUploadingRef: false,
    newRefLabel: "",
    newRefSource: "",
    refDataUrlCache: {},
    refFetchFailures: {},
    lastRefStats: null,
  },
  authors: {
    authors: [],
    selectedAuthorId: "",
    authorRefDataUrl: "",
    isLoadingAuthorRef: false,
  },
  status: {
    isResearching: false,
    scriptLoadingKey: null,
    imageLoadingVariantIdx: null,
    isGeneratingCopy: false,
    errorMsg: "",
  },
};

type AdsGeneratorAction =
  | { type: "setCampaign"; value: string }
  | { type: "togglePlatform"; platform: AdPlatform }
  | { type: "toggleAngle"; angle: AdAngleCategory }
  | { type: "setVariationsPerAngle"; value: number }
  | { type: "setImageProvider"; provider: ImageProvider }
  | { type: "setNamingObjective"; value: string }
  | { type: "setNamingAudience"; value: string }
  | { type: "setNamingOffer"; value: string }
  | { type: "setLandingSummary"; value: LandingSummary | null }
  | { type: "setAdFormats"; formats: CommunityAdFormat[] }
  | {
      type: "setAdFormatProfile";
      profile: CommunityAdFormatProfile | undefined;
    }
  | {
      type: "applyResearchResults";
      discoveries: DiscoveredAdFormat[];
      rationale: string;
      sources: string[];
      profile: CommunityAdFormatProfile | undefined;
    }
  | { type: "scriptGenerated"; key: string; script: AdScript }
  | { type: "copyVariantsGenerated"; variants: AdCopyVariant[] }
  | { type: "setVariantSelection"; idx: number; selection: VariantSelection }
  | { type: "setVariantImage"; idx: number; image: VariantImage }
  | { type: "setVariantSaving"; idx: number; saving: boolean }
  | { type: "setVariantTextOnImage"; idx: number; enabled: boolean }
  | { type: "setVariantOverlayText"; idx: number; text: string }
  | { type: "variantSaved"; idx: number; saved: SavedAdVariant }
  | { type: "setSavedVariants"; list: SavedAdVariant[] }
  | { type: "setIsLoadingSaved"; value: boolean }
  | { type: "savedVariantDeleted"; savedId: string }
  | { type: "setCompetitorRefs"; list: CompetitorReferenceAd[] }
  | { type: "clearCompetitorRefs" }
  | { type: "competitorRefUploaded"; entry: CompetitorReferenceAd }
  | { type: "competitorRefDeleted"; entry: CompetitorReferenceAd }
  | { type: "setIsUploadingRef"; value: boolean }
  | { type: "setNewRefLabel"; value: string }
  | { type: "setNewRefSource"; value: string }
  | { type: "mergeRefDataUrlCache"; updates: Record<string, string> }
  | { type: "mergeRefFetchFailures"; updates: Record<string, string> }
  | { type: "refPrefetched"; imageURL: string; dataUrl: string }
  | { type: "setLastRefStats"; stats: RefStats | null }
  | { type: "setAuthors"; list: Author[] }
  | { type: "setSelectedAuthorId"; id: string }
  | { type: "setAuthorRefDataUrl"; dataUrl: string }
  | { type: "setIsLoadingAuthorRef"; value: boolean }
  | { type: "setIsResearching"; value: boolean }
  | { type: "setScriptLoadingKey"; key: string | null }
  | { type: "setImageLoadingVariantIdx"; idx: number | null }
  | { type: "setIsGeneratingCopy"; value: boolean }
  | { type: "setErrorMsg"; message: string };

const reducer = (
  state: AdsGeneratorState,
  action: AdsGeneratorAction
): AdsGeneratorState => {
  switch (action.type) {
    case "setCampaign":
      return { ...state, inputs: { ...state.inputs, campaign: action.value } };
    case "togglePlatform": {
      const prev = state.inputs.selectedPlatforms;
      const selectedPlatforms = prev.includes(action.platform)
        ? prev.filter((x) => x !== action.platform)
        : [...prev, action.platform];
      return { ...state, inputs: { ...state.inputs, selectedPlatforms } };
    }
    case "toggleAngle": {
      const prev = state.inputs.selectedAngles;
      const selectedAngles = prev.includes(action.angle)
        ? prev.filter((x) => x !== action.angle)
        : [...prev, action.angle];
      return { ...state, inputs: { ...state.inputs, selectedAngles } };
    }
    case "setVariationsPerAngle":
      return {
        ...state,
        inputs: { ...state.inputs, variationsPerAngle: action.value },
      };
    case "setImageProvider":
      return {
        ...state,
        inputs: { ...state.inputs, imageProvider: action.provider },
      };
    case "setNamingObjective":
      return {
        ...state,
        inputs: { ...state.inputs, namingObjective: action.value },
      };
    case "setNamingAudience":
      return {
        ...state,
        inputs: { ...state.inputs, namingAudience: action.value },
      };
    case "setNamingOffer":
      return {
        ...state,
        inputs: { ...state.inputs, namingOffer: action.value },
      };
    case "setLandingSummary":
      return {
        ...state,
        inputs: { ...state.inputs, landingSummary: action.value },
      };
    case "setAdFormats":
      return {
        ...state,
        formats: { ...state.formats, adFormats: action.formats },
      };
    case "setAdFormatProfile":
      return {
        ...state,
        formats: { ...state.formats, adFormatProfile: action.profile },
      };
    case "applyResearchResults":
      return {
        ...state,
        formats: {
          ...state.formats,
          discoveries: action.discoveries,
          researchRationale: action.rationale,
          researchSources: action.sources,
          adFormatProfile: action.profile,
        },
      };
    case "scriptGenerated":
      return {
        ...state,
        generation: {
          ...state.generation,
          activeScripts: {
            ...state.generation.activeScripts,
            [action.key]: action.script,
          },
        },
      };
    case "copyVariantsGenerated":
      // Fresh batch: clear any per-variant state tied to the previous list.
      return {
        ...state,
        generation: {
          ...state.generation,
          variants: action.variants,
          variantSelections: {},
          variantImages: {},
          variantSaving: {},
          savedVariantIds: {},
          variantTextOnImage: {},
          variantOverlayText: {},
        },
      };
    case "setVariantSelection":
      return {
        ...state,
        generation: {
          ...state.generation,
          variantSelections: {
            ...state.generation.variantSelections,
            [action.idx]: action.selection,
          },
        },
      };
    case "setVariantImage":
      return {
        ...state,
        generation: {
          ...state.generation,
          variantImages: {
            ...state.generation.variantImages,
            [action.idx]: action.image,
          },
        },
      };
    case "setVariantSaving":
      return {
        ...state,
        generation: {
          ...state.generation,
          variantSaving: {
            ...state.generation.variantSaving,
            [action.idx]: action.saving,
          },
        },
      };
    case "setVariantTextOnImage":
      return {
        ...state,
        generation: {
          ...state.generation,
          variantTextOnImage: {
            ...state.generation.variantTextOnImage,
            [action.idx]: action.enabled,
          },
        },
      };
    case "setVariantOverlayText":
      return {
        ...state,
        generation: {
          ...state.generation,
          variantOverlayText: {
            ...state.generation.variantOverlayText,
            [action.idx]: action.text,
          },
        },
      };
    case "variantSaved":
      return {
        ...state,
        generation: {
          ...state.generation,
          savedVariantIds: {
            ...state.generation.savedVariantIds,
            [action.idx]: action.saved.id,
          },
        },
        library: {
          ...state.library,
          savedVariants: [action.saved, ...state.library.savedVariants],
        },
      };
    case "setSavedVariants":
      return {
        ...state,
        library: { ...state.library, savedVariants: action.list },
      };
    case "setIsLoadingSaved":
      return {
        ...state,
        library: { ...state.library, isLoadingSaved: action.value },
      };
    case "savedVariantDeleted": {
      const savedVariantIds = { ...state.generation.savedVariantIds };
      Object.entries(savedVariantIds).forEach(([k, v]) => {
        if (v === action.savedId) delete savedVariantIds[Number(k)];
      });
      return {
        ...state,
        library: {
          ...state.library,
          savedVariants: state.library.savedVariants.filter(
            (s) => s.id !== action.savedId
          ),
        },
        generation: { ...state.generation, savedVariantIds },
      };
    }
    case "setCompetitorRefs":
      return {
        ...state,
        refs: { ...state.refs, competitorRefs: action.list },
      };
    case "clearCompetitorRefs":
      return {
        ...state,
        refs: {
          ...state.refs,
          competitorRefs: [],
          refDataUrlCache: {},
          refFetchFailures: {},
        },
      };
    case "competitorRefUploaded":
      return {
        ...state,
        refs: {
          ...state.refs,
          competitorRefs: [...state.refs.competitorRefs, action.entry],
          newRefLabel: "",
          newRefSource: "",
        },
      };
    case "competitorRefDeleted": {
      const refDataUrlCache = { ...state.refs.refDataUrlCache };
      delete refDataUrlCache[action.entry.imageURL];
      return {
        ...state,
        refs: {
          ...state.refs,
          competitorRefs: state.refs.competitorRefs.filter(
            (e) => e.id !== action.entry.id
          ),
          refDataUrlCache,
        },
      };
    }
    case "setIsUploadingRef":
      return {
        ...state,
        refs: { ...state.refs, isUploadingRef: action.value },
      };
    case "setNewRefLabel":
      return { ...state, refs: { ...state.refs, newRefLabel: action.value } };
    case "setNewRefSource":
      return { ...state, refs: { ...state.refs, newRefSource: action.value } };
    case "mergeRefDataUrlCache":
      return {
        ...state,
        refs: {
          ...state.refs,
          refDataUrlCache: { ...state.refs.refDataUrlCache, ...action.updates },
        },
      };
    case "mergeRefFetchFailures":
      return {
        ...state,
        refs: {
          ...state.refs,
          refFetchFailures: {
            ...state.refs.refFetchFailures,
            ...action.updates,
          },
        },
      };
    case "refPrefetched": {
      const refFetchFailures = { ...state.refs.refFetchFailures };
      delete refFetchFailures[action.imageURL];
      return {
        ...state,
        refs: {
          ...state.refs,
          refDataUrlCache: {
            ...state.refs.refDataUrlCache,
            [action.imageURL]: action.dataUrl,
          },
          refFetchFailures,
        },
      };
    }
    case "setLastRefStats":
      return {
        ...state,
        refs: { ...state.refs, lastRefStats: action.stats },
      };
    case "setAuthors":
      return {
        ...state,
        authors: { ...state.authors, authors: action.list },
      };
    case "setSelectedAuthorId":
      return {
        ...state,
        authors: { ...state.authors, selectedAuthorId: action.id },
      };
    case "setAuthorRefDataUrl":
      return {
        ...state,
        authors: { ...state.authors, authorRefDataUrl: action.dataUrl },
      };
    case "setIsLoadingAuthorRef":
      return {
        ...state,
        authors: { ...state.authors, isLoadingAuthorRef: action.value },
      };
    case "setIsResearching":
      return {
        ...state,
        status: { ...state.status, isResearching: action.value },
      };
    case "setScriptLoadingKey":
      return {
        ...state,
        status: { ...state.status, scriptLoadingKey: action.key },
      };
    case "setImageLoadingVariantIdx":
      return {
        ...state,
        status: { ...state.status, imageLoadingVariantIdx: action.idx },
      };
    case "setIsGeneratingCopy":
      return {
        ...state,
        status: { ...state.status, isGeneratingCopy: action.value },
      };
    case "setErrorMsg":
      return {
        ...state,
        status: { ...state.status, errorMsg: action.message },
      };
    default:
      return state;
  }
};

const createActions = (dispatch: Dispatch<AdsGeneratorAction>) => ({
  setCampaign: (value: string) => dispatch({ type: "setCampaign", value }),
  togglePlatform: (platform: AdPlatform) =>
    dispatch({ type: "togglePlatform", platform }),
  toggleAngle: (angle: AdAngleCategory) =>
    dispatch({ type: "toggleAngle", angle }),
  setVariationsPerAngle: (value: number) =>
    dispatch({ type: "setVariationsPerAngle", value }),
  setImageProvider: (provider: ImageProvider) =>
    dispatch({ type: "setImageProvider", provider }),
  setNamingObjective: (value: string) =>
    dispatch({ type: "setNamingObjective", value }),
  setNamingAudience: (value: string) =>
    dispatch({ type: "setNamingAudience", value }),
  setNamingOffer: (value: string) => dispatch({ type: "setNamingOffer", value }),
  setLandingSummary: (value: LandingSummary | null) =>
    dispatch({ type: "setLandingSummary", value }),
  setAdFormats: (formats: CommunityAdFormat[]) =>
    dispatch({ type: "setAdFormats", formats }),
  setAdFormatProfile: (profile: CommunityAdFormatProfile | undefined) =>
    dispatch({ type: "setAdFormatProfile", profile }),
  applyResearchResults: (result: {
    discoveries: DiscoveredAdFormat[];
    rationale: string;
    sources: string[];
    profile: CommunityAdFormatProfile | undefined;
  }) => dispatch({ type: "applyResearchResults", ...result }),
  scriptGenerated: (key: string, script: AdScript) =>
    dispatch({ type: "scriptGenerated", key, script }),
  copyVariantsGenerated: (variants: AdCopyVariant[]) =>
    dispatch({ type: "copyVariantsGenerated", variants }),
  setVariantSelection: (idx: number, selection: VariantSelection) =>
    dispatch({ type: "setVariantSelection", idx, selection }),
  setVariantImage: (idx: number, image: VariantImage) =>
    dispatch({ type: "setVariantImage", idx, image }),
  setVariantSaving: (idx: number, saving: boolean) =>
    dispatch({ type: "setVariantSaving", idx, saving }),
  setVariantTextOnImage: (idx: number, enabled: boolean) =>
    dispatch({ type: "setVariantTextOnImage", idx, enabled }),
  setVariantOverlayText: (idx: number, text: string) =>
    dispatch({ type: "setVariantOverlayText", idx, text }),
  variantSaved: (idx: number, saved: SavedAdVariant) =>
    dispatch({ type: "variantSaved", idx, saved }),
  setSavedVariants: (list: SavedAdVariant[]) =>
    dispatch({ type: "setSavedVariants", list }),
  setIsLoadingSaved: (value: boolean) =>
    dispatch({ type: "setIsLoadingSaved", value }),
  savedVariantDeleted: (savedId: string) =>
    dispatch({ type: "savedVariantDeleted", savedId }),
  setCompetitorRefs: (list: CompetitorReferenceAd[]) =>
    dispatch({ type: "setCompetitorRefs", list }),
  clearCompetitorRefs: () => dispatch({ type: "clearCompetitorRefs" }),
  competitorRefUploaded: (entry: CompetitorReferenceAd) =>
    dispatch({ type: "competitorRefUploaded", entry }),
  competitorRefDeleted: (entry: CompetitorReferenceAd) =>
    dispatch({ type: "competitorRefDeleted", entry }),
  setIsUploadingRef: (value: boolean) =>
    dispatch({ type: "setIsUploadingRef", value }),
  setNewRefLabel: (value: string) => dispatch({ type: "setNewRefLabel", value }),
  setNewRefSource: (value: string) =>
    dispatch({ type: "setNewRefSource", value }),
  mergeRefDataUrlCache: (updates: Record<string, string>) =>
    dispatch({ type: "mergeRefDataUrlCache", updates }),
  mergeRefFetchFailures: (updates: Record<string, string>) =>
    dispatch({ type: "mergeRefFetchFailures", updates }),
  refPrefetched: (imageURL: string, dataUrl: string) =>
    dispatch({ type: "refPrefetched", imageURL, dataUrl }),
  setLastRefStats: (stats: RefStats | null) =>
    dispatch({ type: "setLastRefStats", stats }),
  setAuthors: (list: Author[]) => dispatch({ type: "setAuthors", list }),
  setSelectedAuthorId: (id: string) =>
    dispatch({ type: "setSelectedAuthorId", id }),
  setAuthorRefDataUrl: (dataUrl: string) =>
    dispatch({ type: "setAuthorRefDataUrl", dataUrl }),
  setIsLoadingAuthorRef: (value: boolean) =>
    dispatch({ type: "setIsLoadingAuthorRef", value }),
  setIsResearching: (value: boolean) =>
    dispatch({ type: "setIsResearching", value }),
  setScriptLoadingKey: (key: string | null) =>
    dispatch({ type: "setScriptLoadingKey", key }),
  setImageLoadingVariantIdx: (idx: number | null) =>
    dispatch({ type: "setImageLoadingVariantIdx", idx }),
  setIsGeneratingCopy: (value: boolean) =>
    dispatch({ type: "setIsGeneratingCopy", value }),
  setErrorMsg: (message: string) => dispatch({ type: "setErrorMsg", message }),
});

export type AdsGeneratorActions = ReturnType<typeof createActions>;

export const useAdsGeneratorState = (): {
  state: AdsGeneratorState;
  actions: AdsGeneratorActions;
} => {
  const [state, dispatch] = useReducer(reducer, initialState);
  // Created once via the useState initializer so `actions` keeps a stable
  // identity and effect dependency arrays that include it never retrigger.
  const [actions] = useState(() => createActions(dispatch));
  return { state, actions };
};
